/**
 * 性能优化工具集
 * 包含数据缓存、图片优化、预加载等功能
 */

class PerformanceOptimizer {
  constructor() {
    this.cache = new Map();
    this.imageCache = new Map();
    this.prefetchQueue = [];
    this.isOnline = navigator.onLine;
    this.networkType = this.getNetworkType();
    
    // 监听网络状态变化
    window.addEventListener('online', () => {
      this.isOnline = true;
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
    });

    // 监听网络类型变化
    if ('connection' in navigator) {
      navigator.connection.addEventListener('change', () => {
        this.networkType = this.getNetworkType();
      });
    }
  }

  /**
   * 获取网络类型
   */
  getNetworkType() {
    if ('connection' in navigator) {
      const connection = navigator.connection;
      return {
        effectiveType: connection.effectiveType,
        downlink: connection.downlink,
        rtt: connection.rtt,
        saveData: connection.saveData
      };
    }
    return { effectiveType: '4g', downlink: 10, rtt: 100, saveData: false };
  }

  /**
   * 判断是否为慢速网络
   */
  isSlowNetwork() {
    const { effectiveType, saveData, downlink } = this.networkType;
    return saveData || effectiveType === 'slow-2g' || effectiveType === '2g' || downlink < 1;
  }

  /**
   * 缓存API响应
   */
  cacheResponse(key, data, ttl = 5 * 60 * 1000) { // 默认5分钟
    const expiry = Date.now() + ttl;
    this.cache.set(key, {
      data,
      expiry,
      timestamp: Date.now()
    });
  }

  /**
   * 获取缓存的响应
   */
  getCachedResponse(key) {
    const cached = this.cache.get(key);
    if (!cached) return null;
    
    if (Date.now() > cached.expiry) {
      this.cache.delete(key);
      return null;
    }
    
    return cached.data;
  }

  /**
   * 获取API URL
   * 避免循环依赖，直接实现API URL获取逻辑
   */
  getApiUrl() {
    // 1. 优先使用自定义环境变量
    if (process.env.REACT_APP_API_URL) {
      return process.env.REACT_APP_API_URL;
    }
    
    // 2. 根据访问环境确定后端地址
    const host = window.location.hostname;
    const protocol = window.location.protocol;
    
    // 如果是本地开发环境
    if (host === 'localhost' || host === '127.0.0.1') {
      const port = process.env.STRAPI_PORT || 1337;
      return `${protocol}//localhost:${port}`;
    }
    
    // 生产环境：使用当前域名，不包含端口号
    return `${protocol}//${host}`;
  }

  /**
   * 智能图片URL生成
   * 根据网络状况和设备能力选择合适的图片格式和尺寸
   */
  optimizeImageUrl(originalUrl, options = {}) {
    // 严格检查URL有效性
    if (!originalUrl || originalUrl === null || originalUrl === undefined || originalUrl === '' || typeof originalUrl !== 'string') {
      return null;
    }

    // 检查并修复已经损坏的URL（双重协议问题）
    let cleanUrl = originalUrl;
    if (originalUrl.includes('http://localhost:1337http://localhost:')) {
      // 修复双重localhost URL
      cleanUrl = originalUrl.replace(/^http:\/\/localhost:\d+http:\/\/localhost:\d+/, 'http://localhost:1337');
    }

    const {
      width = 400,
      height = 300,
      quality = this.isSlowNetwork() ? 70 : 85,
      format = this.getSupportedImageFormat(),
      fit = 'cover'
    } = options;

    try {
      let baseUrl;
      
      // 如果URL已经是完整的HTTP URL，直接使用
      if (cleanUrl.startsWith('http://') || cleanUrl.startsWith('https://')) {
        baseUrl = cleanUrl;
      } else {
        // 如果是相对路径，需要构建完整URL
        // 对于上传的文件，使用API服务器的URL而不是前端服务器的URL
        if (cleanUrl.startsWith('/uploads/')) {
          // 动态导入API_URL以避免循环依赖
          const apiUrl = this.getApiUrl();
          baseUrl = `${apiUrl}${cleanUrl}`;
        } else {
          baseUrl = `${window.location.origin}${cleanUrl}`;
        }
      }
      
      // 验证baseUrl是否有效
      if (!baseUrl || baseUrl === 'null' || baseUrl === 'undefined') {
        return originalUrl;
      }
      
      // 如果是外部URL（不在我们的域名下），直接返回不做优化
      const url = new URL(baseUrl);
      const isExternal = !url.hostname.includes('localhost') && 
                        url.hostname !== window.location.hostname;
      if (isExternal) {
        return originalUrl;
      }
      
      // 添加图片优化参数
      url.searchParams.set('w', width);
      url.searchParams.set('h', height);
      url.searchParams.set('q', quality);
      url.searchParams.set('f', format);
      url.searchParams.set('fit', fit);

      return url.toString();
    } catch (error) {
      console.warn('Failed to optimize image URL:', originalUrl, error);
      // 如果URL构造失败，返回原始URL
      return originalUrl;
    }
  }

  /**
   * 获取浏览器支持的最佳图片格式
   */
  getSupportedImageFormat() {
    // 检测WebP支持
    if (this.supportsWebP()) {
      return 'webp';
    }
    
    // 检测AVIF支持
    if (this.supportsAVIF()) {
      return 'avif';
    }
    
    return 'jpeg';
  }

  /**
   * 检测WebP支持
   */
  supportsWebP() {
    if (this._webpSupport !== undefined) return this._webpSupport;
    
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    this._webpSupport = canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
    return this._webpSupport;
  }

  /**
   * 检测AVIF支持
   */
  supportsAVIF() {
    if (this._avifSupport !== undefined) return this._avifSupport;
    
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    this._avifSupport = canvas.toDataURL('image/avif').indexOf('data:image/avif') === 0;
    return this._avifSupport;
  }

  /**
   * 预加载关键图片
   */
  preloadCriticalImages(imageUrls) {
    if (this.isSlowNetwork()) {
      // 慢速网络下只预加载第一张图片
      imageUrls = imageUrls.slice(0, 1);
    }

    imageUrls.forEach(url => {
      if (!this.imageCache.has(url)) {
        const img = new Image();
        img.onload = () => {
          this.imageCache.set(url, true);
        };
        img.onerror = () => {
          this.imageCache.set(url, false);
        };
        img.src = url;
      }
    });
  }

  /**
   * 懒加载图片
   */
  setupLazyLoading(selector = 'img[data-lazy]') {
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const img = entry.target;
              const src = img.dataset.lazy;
              
              if (src) {
                // 优化图片URL
                const optimizedSrc = this.optimizeImageUrl(src, {
                  width: img.width || 400,
                  height: img.height || 300
                });
                
                img.src = optimizedSrc;
                img.classList.add('loading');
                
                img.onload = () => {
                  img.classList.remove('loading');
                  img.classList.add('loaded');
                };
                
                img.onerror = () => {
                  img.classList.remove('loading');
                  img.classList.add('error');
                };
                
                observer.unobserve(img);
              }
            }
          });
        },
        {
          rootMargin: this.isSlowNetwork() ? '10px' : '50px',
          threshold: 0.1
        }
      );

      document.querySelectorAll(selector).forEach(img => {
        observer.observe(img);
      });

      return observer;
    }
  }

  /**
   * 资源预取
   */
  prefetchResource(url, priority = 'low') {
    if (this.isSlowNetwork() && priority === 'low') {
      return; // 慢速网络下跳过低优先级预取
    }

    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    link.as = this.getResourceType(url);
    
    if (priority === 'high') {
      link.rel = 'preload';
    }
    
    document.head.appendChild(link);
  }

  /**
   * 获取资源类型
   */
  getResourceType(url) {
    const extension = url.split('.').pop().toLowerCase();
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif', 'svg'];
    const videoExtensions = ['mp4', 'webm', 'ogg'];
    
    if (imageExtensions.includes(extension)) return 'image';
    if (videoExtensions.includes(extension)) return 'video';
    if (extension === 'css') return 'style';
    if (extension === 'js') return 'script';
    
    return 'fetch';
  }

  /**
   * 批量API请求优化
   */
  async batchApiRequests(requests, maxConcurrent = 2) {
    const results = [];
    const executing = [];

    for (const request of requests) {
      const promise = this.executeRequest(request).then(result => {
        executing.splice(executing.indexOf(promise), 1);
        return result;
      });

      results.push(promise);
      executing.push(promise);

      if (executing.length >= maxConcurrent) {
        await Promise.race(executing);
      }
    }

    return Promise.allSettled(results);
  }

  /**
   * 执行单个请求
   */
  async executeRequest(request) {
    const { url, options = {}, cacheKey, cacheTTL } = request;
    
    // 检查缓存
    if (cacheKey) {
      const cached = this.getCachedResponse(cacheKey);
      if (cached) {
        return cached;
      }
    }

    try {
      const response = await fetch(url, {
        ...options,
        signal: AbortSignal.timeout(this.isSlowNetwork() ? 20000 : 12000)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response received for URL:', url);
        console.error('Response content:', text.substring(0, 200));
        throw new Error('Response is not JSON');
      }

      const data = await response.json();

      // 缓存响应
      if (cacheKey) {
        this.cacheResponse(cacheKey, data, cacheTTL);
      }

      return data;
    } catch (error) {
      console.error('Request failed for URL:', url, error);
      throw error;
    }
  }

  /**
   * 清理过期缓存
   */
  cleanupCache() {
    const now = Date.now();
    for (const [key, value] of this.cache.entries()) {
      if (now > value.expiry) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * 获取性能指标
   */
  getPerformanceMetrics() {
    if ('performance' in window) {
      const navigation = performance.getEntriesByType('navigation')[0];
      const paint = performance.getEntriesByType('paint');
      
      return {
        domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
        loadComplete: navigation.loadEventEnd - navigation.loadEventStart,
        firstPaint: paint.find(p => p.name === 'first-paint')?.startTime,
        firstContentfulPaint: paint.find(p => p.name === 'first-contentful-paint')?.startTime,
        cacheHitRate: this.getCacheHitRate(),
        networkType: this.networkType
      };
    }
    return null;
  }

  /**
   * 获取缓存命中率
   */
  getCacheHitRate() {
    if (this.cache.size === 0) return 0;
    
    let hits = 0;
    for (const [key, value] of this.cache.entries()) {
      if (value.timestamp) hits++;
    }
    
    return (hits / this.cache.size * 100).toFixed(2);
  }
}

// 创建全局实例
export const performanceOptimizer = new PerformanceOptimizer();

// 定期清理缓存
setInterval(() => {
  performanceOptimizer.cleanupCache();
}, 5 * 60 * 1000); // 每5分钟清理一次

export default performanceOptimizer;